import { del, get, post, put } from '@/lib/http';
import { endpoints } from './endpoints';
import type { MobilePersona, Permission } from '@/types';

/** A role as the backend defines it — including workspace-defined ones. */
export interface RoleSummary {
  id: number;
  code: string;
  name: string;
  description?: string;
  /** Built-in. Its code cannot change and it cannot be deleted — permissions still can. */
  systemRole: boolean;
  landingRoute: string;
  mobilePersona: MobilePersona;
  order: number;
  permissions: Permission[];
  /** Active users holding this role, primary or secondary. */
  userCount: number;
}

export interface PermissionInfo {
  id: number;
  code: Permission;
  name: string;
  description?: string;
  groupId: number;
  groupName: string;
}

export interface PermissionGroup {
  id: number;
  code: string;
  name: string;
  permissions: PermissionInfo[];
}

export interface RoleRequest {
  code: string;
  name: string;
  description?: string;
  landingRoute?: string;
  mobilePersona?: MobilePersona;
  order?: number;
  permissions?: Permission[];
}

/**
 * Roles and permissions.
 *
 * This is the whole RBAC surface: the catalogue of what can be granted, the
 * roles that bundle those grants, and the calls that change them. Everything the
 * UI gates on — the sidebar, the route guards, the buttons — resolves back to a
 * permission edited here.
 */
export const rbacApi = {
  roles: () => get<RoleSummary[]>(endpoints.rbac.roles),

  role: (id: number) => get<RoleSummary>(endpoints.rbac.role(id)),

  permissions: () => get<PermissionGroup[]>(endpoints.rbac.permissions),

  create: (body: RoleRequest) => post<RoleSummary, RoleRequest>(endpoints.rbac.roles, body),

  update: (id: number, body: RoleRequest) =>
    put<RoleSummary, RoleRequest>(endpoints.rbac.role(id), body),

  /** Replaces the permission set outright — send the full list, not a delta. */
  setPermissions: (id: number, permissions: Permission[]) =>
    put<RoleSummary, { permissions: Permission[] }>(endpoints.rbac.rolePermissions(id), {
      permissions,
    }),

  remove: (id: number) => del<void>(endpoints.rbac.role(id)),
};
